import { calculateCurrentAge } from './ageUtils';

export const INPUT_COMPARE_KEYS = [
  'chart_no',
  'eye',
  'gender',
  'birth_date',
  'orientation',
  'exam_date',
  'opmr_sphere',
  'opmr_cylinder',
  'opmr_axis',
  'k1',
  'k2',
  'k_axis',
  'acd_preop',
  'wtw',
  'ct',
  'exp_se',
  'casia_acw',
  'casia_clr',
  'casia_ata',
  'casia_acd',
  'used_lens_size',
  'used_lens_sphere',
  'used_lens_cylinder',
  'surgery_date'
];

export const FOLLOW_COMPARE_KEYS = [
  ...INPUT_COMPARE_KEYS.filter(
    (key) => !key.startsWith('used_lens_') && key !== 'surgery_date'
  ),
  'actual_vault_1day',
  'actual_vault_1month',
  'postop_va',
  'follow_note'
];

/** 수정이력 변경내용에 표시할 필드 한글 라벨 */
export const FIELD_LABELS = {
  chart_no: '차트번호',
  eye: 'Eye',
  gender: '성별',
  birth_date: '생년월일',
  orientation: '삽입 방향',
  exam_date: '검사일',
  opmr_sphere: 'OPMR Sphere',
  opmr_cylinder: 'OPMR Cylinder',
  opmr_axis: 'OPMR Axis',
  k1: 'K1',
  k2: 'K2',
  k_axis: 'K Axis',
  acd_preop: 'ACD',
  wtw: 'WTW',
  ct: 'CT',
  exp_se: 'Expected SE',
  casia_acw: 'CASIA ACW',
  casia_clr: 'CASIA CLR',
  casia_ata: 'CASIA ATA',
  casia_acd: 'CASIA ACD',
  used_lens_size: '실제 사용 렌즈 Size',
  used_lens_sphere: '실제 사용 렌즈 Sphere',
  used_lens_cylinder: '실제 사용 렌즈 Cylinder',
  surgery_date: '수술일',
  actual_vault_1day: '실측 1일 vault',
  actual_vault_1month: '실측 1개월 vault',
  postop_va: '수술 후 시력',
  follow_note: '비고'
};

export function formatDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function createInitialForm() {
  return {
    case_id: '',
    chart_no: '',
    eye: 'OD',
    gender: 'F',
    birth_date: '',
    age_years: '',
    age_group: '',
    exam_date: formatDate(),
    orientation: '수평',
    opmr_sphere: '',
    opmr_cylinder: '',
    opmr_axis: '',
    k1: '',
    k_axis: '',
    k2: '',
    acd_preop: '',
    wtw: '',
    ct: '',
    exp_se: '',
    casia_acw: '',
    casia_clr: '',
    casia_ata: '',
    casia_acd: '',
    lens_size: 12.1,
    lens_sphere: '',
    lens_cylinder: '',
    used_lens_size: '',
    used_lens_sphere: '',
    used_lens_cylinder: '',
    actual_vault_1day: '',
    actual_vault_1month: '',
    postop_va: '',
    surgery_date: '',
    follow_note: ''
  };
}

export function getAgeGroup(ageYears) {
  if (!Number.isFinite(ageYears) || ageYears < 0) return '';
  const decade = Math.floor(ageYears / 10) * 10;
  return `${decade}대`;
}

export function applyBirthAndExamAge(form, birthDate, examDate) {
  const age = calculateCurrentAge(birthDate, examDate ? new Date(`${examDate}T00:00:00`) : new Date());
  return {
    ...form,
    birth_date: birthDate,
    exam_date: examDate ?? form.exam_date,
    age_years: age === null ? '' : String(age),
    age_group: age === null ? '' : getAgeGroup(age)
  };
}

export function getSessionUser() {
  const userSession = sessionStorage.getItem('user');
  return userSession ? JSON.parse(userSession) : null;
}

export function isBlank(value) {
  return value === undefined || value === null || String(value).trim() === '';
}

export function isRecommendAction(row) {
  const type = row?.actionType || row?.action_type || 'recommend';
  return type === 'recommend' || type === 'predict';
}

export function isFollowupAction(row) {
  const type = row?.actionType || row?.action_type;
  return type === 'followup';
}

export function pickLatestUsedLens(rows) {
  for (const row of rows || []) {
    if (row.usedLensSize != null || row.used_lens_size != null
      || row.usedLensSphere != null || row.used_lens_sphere != null) {
      return {
        used_lens_size: row.usedLensSize != null
          ? String(row.usedLensSize)
          : (row.used_lens_size != null ? String(row.used_lens_size) : ''),
        used_lens_sphere: row.usedLensSphere != null
          ? String(row.usedLensSphere)
          : (row.used_lens_sphere != null ? String(row.used_lens_sphere) : ''),
        used_lens_cylinder: row.usedLensCylinder != null
          ? String(row.usedLensCylinder)
          : (row.used_lens_cylinder != null ? String(row.used_lens_cylinder) : '')
      };
    }
  }
  return {
    used_lens_size: '',
    used_lens_sphere: '',
    used_lens_cylinder: ''
  };
}

export function buildPatientPayload(form, computedAge) {
  const base = {
    chart_no: form.chart_no || undefined,
    eye: form.eye,
    gender: form.gender,
    age_years: computedAge,
    orientation: form.orientation,
    sphere: Number(form.opmr_sphere),
    cylinder: Number(form.opmr_cylinder),
    axis: Number(form.opmr_axis),
    k1: Number(form.k1),
    k_axis: Number(form.k_axis),
    k2: Number(form.k2),
    k2_axis: Number(form.k_axis),
    acd_preop: Number(form.acd_preop),
    ct: Number(form.ct),
    wtw: Number(form.wtw),
    exp_se: Number(form.exp_se || 0)
  };

  const casia = {
    casia_h_acw_mm: form.orientation === '수평' ? Number(form.casia_acw) : undefined,
    casia_h_clr_um: form.orientation === '수평' ? Number(form.casia_clr) : undefined,
    casia_h_ata_mm: form.orientation === '수평' ? Number(form.casia_ata) : undefined,
    casia_h_acd_mm: form.orientation === '수평' ? Number(form.casia_acd) : undefined,
    casia_v_acw_mm: form.orientation === '수직' ? Number(form.casia_acw) : undefined,
    casia_v_clr_um: form.orientation === '수직' ? Number(form.casia_clr) : undefined,
    casia_v_ata_mm: form.orientation === '수직' ? Number(form.casia_ata) : undefined,
    casia_v_acd_mm: form.orientation === '수직' ? Number(form.casia_acd) : undefined
  };

  return { ...base, ...casia };
}


export function inputsEqual(a, b, keys = INPUT_COMPARE_KEYS) {
  if (!a || !b) return false;
  return keys.every((key) => String(a[key] ?? '').trim() === String(b[key] ?? '').trim());
}

export function buildChangeSummary(prev, next, keys = INPUT_COMPARE_KEYS) {
  if (!prev) return '신규 등록';
  const changes = [];
  keys.forEach((key) => {
    const before = String(prev[key] ?? '').trim();
    const after = String(next[key] ?? '').trim();
    if (before !== after) {
      const label = FIELD_LABELS[key] || key;
      changes.push(`${label}: ${before || '—'} → ${after || '—'}`);
    }
  });
  if (!changes.length) return '입력 변경 없음';
  // 표 셀에서는 한 줄 연결보다 줄바꿈이 스캔하기 쉽다.
  return changes.join('\n');
}

/**
 * exam-history 응답을 List 행으로 변환한다.
 * 동일 chart+eye+case_id 묶음에서 최신 1건만 남기고, 팔로우 존재 여부를 표시한다.
 * seed(raw)는 최신 recommend 를 우선한다.
 */
export function toLatestListRows(historyRows) {
  const map = new Map();

  (historyRows || []).forEach((row) => {
    const chartNo = String(row.chartNo ?? row.chart_no ?? '').trim();
    const eye = String(row.eye || 'OD').trim() || 'OD';
    const caseId = String(row.caseId ?? row.case_id ?? '').trim();
    if (!chartNo) return;

    // case_id 가 아직 없는 구데이터는 chart+eye 로만 묶는다.
    const key = caseId ? `${chartNo}::${eye}::${caseId}` : `${chartNo}::${eye}`;
    const prev = map.get(key);
    const seq = Number(row.examId ?? row.seq_no ?? 0);
    const actionType = row.actionType || row.action_type || 'recommend';
    const isFollow = actionType === 'followup';

    if (!prev) {
      map.set(key, {
        key,
        caseId,
        chartNo,
        eye,
        gender: row.gender || '',
        birthDate: row.birthDate || row.birth_date || '',
        ageYears: row.ageYears ?? row.age_years ?? '',
        ageGroup: getAgeGroup(Number(row.ageYears ?? row.age_years)),
        lastDt: (row.examDt || row.create_dt || '').slice(0, 10),
        examDt: row.examDt || '',
        seq,
        lastActionType: actionType,
        hasFollowup: isFollow,
        recommendRaw: isRecommendAction(row) ? row : null,
        raw: row
      });
      return;
    }

    const next = {
      ...prev,
      caseId: prev.caseId || caseId,
      hasFollowup: prev.hasFollowup || isFollow
    };

    if (seq > prev.seq) {
      next.lastDt = (row.examDt || row.create_dt || '').slice(0, 10);
      next.examDt = row.examDt || '';
      next.seq = seq;
      next.lastActionType = actionType;
      next.raw = row;
    }

    if (isRecommendAction(row)) {
      const prevRecSeq = Number(next.recommendRaw?.examId ?? next.recommendRaw?.seq_no ?? 0);
      if (!next.recommendRaw || seq > prevRecSeq) {
        next.recommendRaw = row;
        next.gender = row.gender || next.gender;
        next.birthDate = row.birthDate || row.birth_date || next.birthDate;
        next.ageYears = row.ageYears ?? row.age_years ?? next.ageYears;
        next.ageGroup = getAgeGroup(Number(next.ageYears));
      }
    }

    map.set(key, next);
  });

  return Array.from(map.values())
    .map((row) => ({
      ...row,
      raw: row.recommendRaw || row.raw
    }))
    .sort((a, b) => b.seq - a.seq);
}

export function historyRowToForm(row, baseForm = createInitialForm()) {
  if (!row) return baseForm;

  const birth = row.birthDate || row.birth_date || '';
  const examDate = row.examDate
    || row.exam_date
    || (row.examDt || '').slice(0, 10)
    || formatDate();
  const next = {
    ...baseForm,
    case_id: String(row.caseId ?? row.case_id ?? baseForm.case_id ?? ''),
    chart_no: String(row.chartNo ?? row.chart_no ?? ''),
    eye: row.eye || 'OD',
    gender: row.gender || 'F',
    orientation: row.orientation || '수평',
    opmr_sphere: row.opmrSphere != null ? String(row.opmrSphere) : '',
    opmr_cylinder: row.opmrCylinder != null ? String(row.opmrCylinder) : '',
    opmr_axis: row.opmrAxis != null ? String(row.opmrAxis) : '',
    k1: row.k1 != null ? String(row.k1) : '',
    k2: row.k2 != null ? String(row.k2) : '',
    k_axis: row.kAxis != null ? String(row.kAxis) : '',
    acd_preop: row.acdPreop != null ? String(row.acdPreop) : '',
    wtw: row.wtw != null ? String(row.wtw) : '',
    ct: row.ct != null ? String(row.ct) : '',
    exp_se: row.expSe != null ? String(row.expSe) : '',
    casia_acw: row.casiaAcw != null ? String(row.casiaAcw) : '',
    casia_clr: row.casiaClr != null ? String(row.casiaClr) : '',
    casia_ata: row.casiaAta != null ? String(row.casiaAta) : '',
    casia_acd: row.casiaAcd != null ? String(row.casiaAcd) : '',
    lens_size: row.resultLensSize != null ? row.resultLensSize : baseForm.lens_size,
    lens_sphere: row.resultLensSphere != null ? String(row.resultLensSphere) : '',
    used_lens_size: row.usedLensSize != null ? String(row.usedLensSize) : '',
    used_lens_sphere: row.usedLensSphere != null ? String(row.usedLensSphere) : '',
    used_lens_cylinder: row.usedLensCylinder != null ? String(row.usedLensCylinder) : '',
    surgery_date: row.surgeryDate || row.surgery_date || '',
    actual_vault_1day: row.actualVault1DayUm != null ? String(row.actualVault1DayUm) : '',
    actual_vault_1month: row.actualVault1MonthUm != null ? String(row.actualVault1MonthUm) : '',
    postop_va: row.postopVa != null && String(row.postopVa).trim() !== ''
      ? String(row.postopVa).trim()
      : (row.postop_va != null ? String(row.postop_va).trim() : ''),
    follow_note: row.followNote || row.follow_note || ''
  };

  return applyBirthAndExamAge(next, birth, examDate);
}
